<template>
  <div class="relative">
    <NuxtLayout name="order-processing">
      <template #tabs>
        <OrderProcessingStepper :tab="activeTab" @navigate="activeTab = $event" />
      </template>

      <div class="bg-[#F0FBFB]">
        <div
          :class="{
            'w-full md:max-w-6xl mx-auto': activeTab !== 1,
            'w-full': activeTab === 1,
          }"
        >
          <Transition :name="transitionName">
            <div v-show="activeTab === 1" key="tab1">
              <OrderProcessingAccount @userLoggedIn="handleNext" @moveToNextStep="handleNext" />
            </div>
          </Transition>

          <Transition :name="transitionName">
            <div
              v-if="activeTab === 2"
              key="tab2"
              class="flex flex-col lg:flex-row justify-between items-center lg:items-start md:p-6 md:pb-16 bg-[#F0FBFB]"
            >
              <div class="w-full lg:w-[465px] md:p-2 p-4 pt-7">
                <LazyOrderProcessingPatientDetails
                  :cartItems="cartItems"
                  :totalItems="totalItems"
                  :totalPrice="totalPrice"
                  :oldPatientCollection="patientsCollection"
                  :isSubmitting="shouldSubmitForm"
                  :serverErrors="serverErrors"
                  @update:patientsCollection="handlePatientsCollection"
                  @submit="patientFormSubmittion.handleFormSubmit"
                  @validationError="(hasErrors: boolean) => hasErrors && (shouldSubmitForm = false)"
                />
              </div>

              <div class="w-full lg:w-[409px] space-y-6 md:p-6 p-4 pt-7 md:sticky md:top-24 md:z-40">
                <LazyOrderProcessingSummaryCard
                  :loading="createOrderProcessing"
                  :show-button="showButton"
                  :button-text="buttonText"
                  :cartItems="cartItems"
                  :totalItems="totalItems"
                  :totalPrice="totalPrice"
                  :patientsCollection="patientsCollection"
                  @click="patientFormSubmittion.triggerFormSubmit"
                />
              </div>
            </div>

            <div
              v-else-if="activeTab === 3"
              key="tab3"
              class="flex flex-col lg:flex-row justify-between items-center lg:items-start md:p-6 md:pb-16 bg-[#F0FBFB]"
            >
              <div class="w-full lg:w-[465px] md:p-6 p-4 pt-7">
                <LazyOrderProcessingPaymentMethods
                  ref="paymentMethodsRef"
                  v-model:processing="paymentProcessing"
                  @onPaymentSuccess="handleNext"
                >
                  <template #fallback>
                    <p class="text-gray-600">{{ $t("orderProcessing.payment.loading") }}</p>
                  </template>
                </LazyOrderProcessingPaymentMethods>
              </div>

              <div class="w-full lg:w-[409px] space-y-6 md:p-6 p-4 pt-7">
                <LazyOrderProcessingSummaryCard
                  :show-coupon-widget="false"
                  :cartItems="cartItems"
                  :totalItems="totalItems"
                  :totalPrice="totalPrice"
                  :patientsCollection="patientsCollection"
                />
                <LazyOrderProcessingPaymentControls @click="handlePaymentClick" :loading="paymentProcessing" />
              </div>
            </div>

            <div v-else-if="activeTab === 4" key="tab4" class="md:p-6 md:pb-16">
              <LazyOrderProcessingThankyou />
            </div>
          </Transition>
        </div>
      </div>
    </NuxtLayout>

    <div v-if="!isValidCart" class="fixed inset-0 flex justify-center items-center bg-white/80 z-50">
      <div class="text-center">
        <NuxtImg class="w-20 mb-4 mx-auto" src="/icons/loading.svg" />
        <p class="text-gray-600">{{ $t("orderProcessing.cart.emptyRedirecting") }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PaymentMethods from "~/components/order-processing/PaymentMethods.vue";
import { useCartCheckoutSession } from "~/composables/order-processing/useCartCheckoutSession";
import { useOrderPayload } from "~/composables/order-processing/useOrderPayload";
import useClearCartCheckoutSession from "~/composables/useClearCartChecoutSession";
import { useOrderStore } from "~/stores/order";
import type { PatientCollection } from "~/types/order-processing";

const { t } = useI18n();

const cartStore = useCartStore();
const profileStore = useProfileStore();
const { cartItems, totalItems, totalPrice } = toRefs(cartStore);
const { prepareOrderPayload } = useOrderPayload();
const { createOrder } = useCartCheckoutSession();

definePageMeta({
  layout: false,
});

useHead({
  title: t("orderProcessing.meta.pageTitle"),
});

const prev = ref<boolean>(false);
const activeTab = ref<number>(1);
const isValidCart = ref<boolean>(true);

const { loggedIn } = toRefs(profileStore);

onMounted(async () => {
  await nextTick();

  if (totalItems.value === 0) {
    isValidCart.value = false;
    const locale = switchBlogLocale();
    navigateTo(`/${locale}/`, { replace: true });
    return;
  }

  if (loggedIn.value && activeTab.value === 1) {
    activeTab.value = 2;
  }
});

const orderStore = useOrderStore();
const { patientsCollection } = toRefs(orderStore);

const handlePatientsCollection = (data: PatientCollection) => {
  orderStore.updatePatientsCollection(data);
};

const scrollToView = () => {
  window.scrollTo({
    top: 0,
    //behavior: "smooth",
  });
};

const handleNext = () => {
  if (activeTab.value < 4) {
    activeTab.value++;
  }
};

const showButton = computed(() => activeTab.value === 2);

const buttonText = computed(() => {
  if (activeTab.value == 2) {
    return t("orderProcessing.summary.proceedToPayment");
  }

  return "";
});

watch(activeTab, (newVal, prevVal) => {
  prev.value = newVal > prevVal;

  scrollToView();
});

const transitionName = computed(() => (!prev.value ? "slide-fade-back" : "slide-fade"));

//Form Submission
const shouldSubmitForm = ref(false);
const serverErrors = ref<Record<string, string[]> | null>(null);
const patientFormSubmittion = {
  handleFormSubmit: async (values: any) => {
    try {
      serverErrors.value = null;
      createOrderProcessing.value = true;
      if (shouldSubmitForm.value) {
        shouldSubmitForm.value = false;
        const payload = prepareOrderPayload();
        const response = await createOrder(payload as Object);
        if (response) {
          handleNext();
        }
      }
    } catch (error: any) {
      console.error("Order creation failed:", error);
      serverErrors.value = error?.data?.errors ?? null;
      shouldSubmitForm.value = true;
      createOrderProcessing.value = false;
    } finally {
      createOrderProcessing.value = false;
    }
  },

  triggerFormSubmit: () => {
    shouldSubmitForm.value = true;
    const form = document.querySelector("form#patient-details-form");
    form?.dispatchEvent(new Event("submit", { cancelable: true }));
  },
};

const paymentMethodsRef = ref<InstanceType<typeof PaymentMethods>>();
const paymentProcessing = ref(false);
const createOrderProcessing = ref(false);

const handlePaymentClick = () => {
  if (paymentMethodsRef.value && typeof paymentMethodsRef.value.processStripePayment === "function") {
    paymentMethodsRef.value.processStripePayment();
  } else {
    console.warn("Payment method not ready");
  }
};

const { clearSession } = useClearCartCheckoutSession();

onBeforeRouteLeave((to, from) => {
  if (!to.path.includes("/order-processing")) {
    clearSession();
  }
});

onUnmounted(() => {
  clearSession();
});

/* onMounted(() => {
    console.log('onMounted');
    scrollToView();
}); */
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-back-enter-active {
  transition: all 0.3s ease-in-out;
}

.slide-fade-leave-active,
.slide-fade-back-leave-active {
  transition: all 0.3s ease-in-out;
  position: absolute;
  width: 100%;
}

.slide-fade-enter-from {
  transform: translateX(100%);
}

.slide-fade-leave-to {
  transform: translateX(-100%);
}

.slide-fade-back-enter-from {
  transform: translateX(-100%);
}

.slide-fade-back-leave-to {
  transform: translateX(100%);
}
</style>
